import { useQuery } from '@tanstack/react-query'
import { AllowanceTransfer, MaxAllowanceTransferAmount, PermitSingle, permit2Address } from '@uniswap/permit2-sdk'
import { CurrencyAmount, Token } from '@uniswap/sdk-core'
import { BigNumber, Contract, providers } from 'ethers/lib/ethers'
import { PERMIT2_ABI } from 'uniswap/src/abis/permit2'
import { useUniswapContextSelector } from 'uniswap/src/contexts/UniswapContext'
import { NullablePermit } from 'uniswap/src/data/tradingApi/__generated__'
import { UniverseChainId } from 'uniswap/src/features/chains/types'

const PERMIT_EXPIRATION_SECONDS = 30 * 24 * 60 * 60
const PERMIT_SIGNATURE_EXPIRATION_SECONDS = 30 * 60
const PERMIT_EXPIRATION_BUFFER_SECONDS = 60
const PERMIT_ALLOWANCE_POLL_INTERVAL_MS = 12_000

function useUnavailableProvider(): undefined {
  return undefined
}

export function createLocalFewV2PermitData({
  chainId,
  currentTimestamp,
  nonce,
  spender,
  token,
}: {
  chainId: number
  currentTimestamp: number
  nonce: BigNumber
  spender: string
  token: Token
}): NullablePermit {
  const permit: PermitSingle = {
    details: {
      token: token.address,
      amount: MaxAllowanceTransferAmount,
      expiration: currentTimestamp + PERMIT_EXPIRATION_SECONDS,
      nonce,
    },
    spender,
    sigDeadline: currentTimestamp + PERMIT_SIGNATURE_EXPIRATION_SECONDS,
  }

  return AllowanceTransfer.getPermitData(permit, permit2Address(chainId), chainId)
}

async function getLocalFewV2PermitData({
  amount,
  owner,
  provider,
  spender,
}: {
  amount: CurrencyAmount<Token>
  owner: string
  provider: providers.JsonRpcProvider
  spender: string
}): Promise<NullablePermit> {
  const chainId = amount.currency.chainId
  const permit2 = new Contract(permit2Address(chainId), PERMIT2_ABI, provider)
  const [allowance, expiration, nonce] = await permit2.allowance(owner, amount.currency.address, spender)
  const currentTimestamp = Math.floor(Date.now() / 1000)
  const allowanceIsUsable =
    BigNumber.from(allowance).gte(amount.quotient.toString()) &&
    Number(expiration) > currentTimestamp + PERMIT_EXPIRATION_BUFFER_SECONDS

  if (allowanceIsUsable) {
    return null
  }

  return createLocalFewV2PermitData({
    chainId,
    currentTimestamp,
    nonce: BigNumber.from(nonce),
    spender,
    token: amount.currency,
  })
}

export function useLocalFewV2PermitData({
  amount,
  owner,
  spender,
}: {
  amount?: CurrencyAmount<Token>
  owner?: string
  spender?: string
}): { permitData: NullablePermit | undefined; error: Error | null; isLoading: boolean } {
  const useProviderHook = useUniswapContextSelector((context) => context.useProviderHook) ?? useUnavailableProvider
  const provider = useProviderHook(amount?.currency.chainId ?? UniverseChainId.Mainnet)
  const enabled = Boolean(amount && owner && provider && spender)
  const query = useQuery({
    queryKey: [
      'localFewV2Permit2Allowance',
      amount?.currency.chainId,
      amount?.currency.address,
      amount?.quotient.toString(),
      owner,
      spender,
    ],
    queryFn: async () => {
      if (!amount || !owner || !provider || !spender) {
        return null
      }
      return getLocalFewV2PermitData({ amount, owner, provider, spender })
    },
    enabled,
    refetchInterval: PERMIT_ALLOWANCE_POLL_INTERVAL_MS,
    staleTime: PERMIT_ALLOWANCE_POLL_INTERVAL_MS,
  })

  return {
    permitData: enabled ? query.data : null,
    error: query.error,
    isLoading: enabled && query.isLoading,
  }
}
